// SWMM's own date/time literal formats. UTC-anchored, not the machine's own
// local timezone — every `Date` here is built and read via its UTC-prefixed
// methods consistently, so a simulated date/time is the same absolute
// instant on every machine regardless of its own timezone.

const MONTH_ABBR = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function isValidSwmmDate(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  const daysInMonth = month === 2 && isLeapYear(year) ? 29 : DAYS_PER_MONTH[month - 1];
  return Number.isInteger(day) && day >= 1 && day <= daysInMonth;
}

/**
 * M_D_Y token order only, matching the real engine's own fallback parse:
 * `-` and `/` are both valid separators (need not even match each other), a
 * 3-letter month name is tried when the numeric parse fails, and a 2-digit
 * year is taken AS-IS (e.g. "1/1/98" -> year 98, NOT 1998) — real
 * `datetime_encodeDate` has no century inference at all, unlike a plain JS
 * `Date` constructor.
 */
export function parseSwmmDateParts(
  token: string | undefined
): { year: number; month: number; day: number } | null {
  if (!token) return null;
  const numeric = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/.exec(token);
  if (numeric) {
    const month = Number(numeric[1]);
    const day = Number(numeric[2]);
    const year = Number(numeric[3]);
    if (!month || !day || !year || !isValidSwmmDate(year, month, day)) return null;
    return { year, month, day };
  }
  const withMonthName = /^([A-Za-z]{3})[/-](\d{1,2})[/-](\d{2,4})$/.exec(token);
  if (withMonthName) {
    const month = MONTH_ABBR.indexOf(withMonthName[1].toUpperCase()) + 1;
    const day = Number(withMonthName[2]);
    const year = Number(withMonthName[3]);
    if (!month || !day || !year || !isValidSwmmDate(year, month, day)) return null;
    return { year, month, day };
  }
  return null;
}

/** Literal year, no 2-digit-year promotion — `Date.UTC`/`new Date(y,...)` both auto-promote a 0-99 year to 19xx, so this uses `setUTCFullYear` instead, which never promotes. */
export function dateFromSwmmParts(
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0
): Date {
  const date = new Date(
    Date.UTC(year < 100 ? year + 1900 : year, month - 1, day, hours, minutes, seconds)
  );
  if (year < 100) date.setUTCFullYear(year);
  return date;
}

export function parseSwmmDate(
  dateStr: string | undefined,
  timeStr: string | undefined
): Date | null {
  const parts = parseSwmmDateParts(dateStr);
  if (!parts) return null;
  const [hours = 0, minutes = 0, seconds = 0] = (timeStr ?? "00:00:00").split(":").map(Number);
  // A negative or unparseable component must fall back to 0 (midnight), not
  // feed NaN/negative into Date.UTC — matches the real engine's own
  // validity check, which has no upper bound (only >= 0).
  const validTime = [hours, minutes, seconds].every((v) => Number.isFinite(v) && v >= 0);
  const [h, m, s] = validTime ? [hours, minutes, seconds] : [0, 0, 0];
  return dateFromSwmmParts(parts.year, parts.month, parts.day, h, m, s);
}

/**
 * ROUTING_STEP/LENGTHENING_STEP only: a bare number (no `:`) is SECONDS.
 * `REPORT_STEP`/`WET_STEP`/`DRY_STEP` use a genuinely different real
 * convention where a bare number is HOURS — see `parseSwmmHourStep`, do not
 * conflate the two.
 */
export function parseSwmmDuration(value: string | undefined): number | null {
  if (!value) return null;
  if (!value.includes(":")) {
    const seconds = Number(value);
    if (Number.isNaN(seconds)) return null;
    return seconds > 0 ? seconds * 1000 : null;
  }
  return parseSwmmColonDuration(value);
}

/** REPORT_STEP/WET_STEP/DRY_STEP only: a bare number is decimal HOURS, not seconds — see `parseSwmmDuration`. */
export function parseSwmmHourStep(value: string | undefined): number | null {
  if (!value) return null;
  if (!value.includes(":")) {
    const hours = Number(value);
    if (Number.isNaN(hours)) return null;
    const ms = hours * 3600 * 1000;
    return ms > 0 ? ms : null;
  }
  return parseSwmmColonDuration(value);
}

function parseSwmmColonDuration(value: string): number | null {
  const parts = value.split(":").map(Number);
  if (parts.some((p) => Number.isNaN(p))) return null;
  const [hours = 0, minutes = 0, seconds = 0] = parts;
  const ms = ((hours * 60 + minutes) * 60 + seconds) * 1000;
  return ms > 0 ? ms : null;
}

/** Tries the token as a date FIRST, matching the real reader's own order — a caller converting the result to a `Date` must use `dateFromSwmmParts`, never a raw `Date.UTC`/`new Date(y,...)` call, to avoid its 2-digit-year promotion. */
export function parseSwmmCalendarDate(
  token: string | undefined
): { year: number; month: number; day: number } | null {
  return parseSwmmDateParts(token);
}

/** Deliberately NOT capped at 24 — a value past 24 means "elapsed hours since the series' reference date" and must roll into the next calendar day, matching the real engine. */
export function parseSwmmElapsedHours(token: string | undefined): number | null {
  if (token === undefined) return null;
  const asNumber = Number(token);
  if (!Number.isNaN(asNumber) && token.trim() !== "") return asNumber;
  const parts = token.split(":").map(Number);
  if (parts.length < 2 || parts.some((p) => Number.isNaN(p))) return null;
  const [hours, minutes = 0, seconds = 0] = parts;
  return hours + minutes / 60 + seconds / 3600;
}

export const MS_PER_DAY = 86_400_000;

/**
 * The calendar date only (midnight), floored down from a raw instant that
 * may still carry a time-of-day fraction. Using the unfloored instant
 * instead is NOT equivalent: `[CONTROLS]` TIMEOPEN/TIMECLOSED premises
 * compute `now - timeLastSet`, which at t=0 only equals 0 when START_TIME
 * is exactly midnight — any other START_TIME lets a rule fire immediately
 * at t=0 that should only fire after its own threshold. Found and fixed
 * independently twice (once per routing method), now shared here so it
 * can't drift back out of sync between them.
 */
export function startOfDayDays(instantMs: number): number {
  return Math.floor(instantMs / MS_PER_DAY);
}

// A bit-for-bit port of the real engine's own calendar representation: days
// since 1899-12-30 as a double, time-of-day as a day FRACTION. This rounds
// DIFFERENTLY from this port's own integer-millisecond `Date` clock — any
// consumer the real engine evaluates on this representation must be
// evaluated on it here too, not on the ms clock, or results diverge from
// the real engine at the rounding-noise level.

export const SWMM_DATE_DELTA = 693594;

export function swmmEncodeDate(year: number, month: number, day: number): number {
  if (!isValidSwmmDate(year, month, day)) return -SWMM_DATE_DELTA;
  const leap = isLeapYear(year) ? 1 : 0;
  let d = day;
  for (let j = 0; j < month - 1; j++) d += DAYS_PER_MONTH[j] + (leap && j === 1 ? 1 : 0);
  const i = year - 1;
  // Real `i/4 - i/100 + i/400` is C INTEGER division (truncating) — must
  // use Math.trunc, not plain JS division.
  return (
    i * 365 + Math.trunc(i / 4) - Math.trunc(i / 100) + Math.trunc(i / 400) + d - SWMM_DATE_DELTA
  );
}

export function swmmEncodeTime(hour: number, minute: number, second: number): number {
  if (hour < 0 || minute < 0 || second < 0) return 0;
  return (hour * 3600 + minute * 60 + second) / 86400;
}

/** Kept separate from `parseSwmmElapsedHours` (which returns hours for the ms-based clock) because the two round differently: `7/86400` is one division, `(7/3600)/24` is two. */
export function parseSwmmTimeDays(token: string | undefined): number | null {
  if (token === undefined) return null;
  const trimmed = token.trim();
  if (trimmed !== "" && /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed)) {
    return Number(trimmed) / 24;
  }
  const m = /^\s*([+-]?\d+)(?::([+-]?\d+))?(?::([+-]?\d+))?/.exec(trimmed);
  if (!m) return null;
  const hr = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  const sec = m[3] === undefined ? 0 : Number(m[3]);
  if (hr < 0 || min < 0 || sec < 0) return null;
  return swmmEncodeTime(hr, min, sec);
}

export function swmmDaysFromDate(date: Date): number {
  return (
    swmmEncodeDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()) +
    swmmEncodeTime(date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds())
  );
}

export function swmmDecodeTime(days: number): { hour: number; minute: number; second: number } {
  const fracDay = (days - Math.floor(days)) * 86400;
  let secs = Math.floor(fracDay + 0.5);
  if (secs >= 86400) secs = 86399;
  const mins = Math.trunc(secs / 60);
  const second = secs - mins * 60;
  let hour = Math.trunc(mins / 60);
  const minute = mins - hour * 60;
  if (hour > 23) hour = 0;
  return { hour, minute, second };
}

export function swmmAddSeconds(days: number, seconds: number): number {
  const d = Math.floor(days);
  const { hour, minute, second } = swmmDecodeTime(days);
  return d + (3600 * hour + 60 * minute + second + seconds) / 86400;
}

/** The extra 1ms nudges an exact boundary instant PAST it before the whole-second rounding, matching the real engine. */
export function swmmDateTimeAtElapsed(startDays: number, elapsedMs: number): number {
  return swmmAddSeconds(startDays, (elapsedMs + 1) / 1000);
}

export function swmmDayOfWeek(days: number): number {
  const t = Math.floor(days) + SWMM_DATE_DELTA;
  return (t % 7) + 1;
}

export function swmmMonthOfYear(days: number): number {
  return new Date((Math.floor(days) - 25569) * MS_PER_DAY).getUTCMonth() + 1;
}
